import { useBlotterGroupingToggling } from '@talos/kyoko';
import type { UseBlotterTableUtilitiesOutput } from '@talos/kyoko/src/components/BlotterTable/types';
import type { StateUpdatedEvent } from 'ag-grid-community';
import { useRowGroupVariantSpecs } from './useRowGroupVariantOptions';

interface UsePositionsSubAccountsBlotterGroupingParams {
  setRowGroupColumns: UseBlotterTableUtilitiesOutput<unknown>['setRowGroupColumns'];
  wrap?: boolean;
  useMobileLabels?: boolean;
}

interface UsePositionsSubAccountsBlotterGroupingOutput {
  onGroupingStateUpdated: (event: StateUpdatedEvent) => void;
  groupingToggleButtons: ReturnType<typeof useBlotterGroupingToggling>['groupingToggleButtons'];
}

/**
 * Shared row-group toggling for desktop and mobile sub-account positions blotters.
 *
 * Wire `onGroupingStateUpdated` into grid state via `useCallbackRef` before `useWsBlotterTable`:
 *
 * ```ts
 * const handleGridStateUpdated = useCallbackRef((event) => {
 *   onGroupingStateUpdated(event);
 *   maybeModifyRequestOnStateUpdated(event);
 * });
 * const blotterTable = useWsBlotterTable({
 *   gridOptions: { ...blotterSpecToUse.gridOptionsOverlay, onStateUpdated: handleGridStateUpdated },
 * });
 * const { onGroupingStateUpdated } = usePositionsSubAccountsBlotterGrouping({
 *   setRowGroupColumns: blotterTable.setRowGroupColumns,
 * });
 * ```
 */
export function usePositionsSubAccountsBlotterGrouping({
  setRowGroupColumns,
  wrap,
  useMobileLabels,
}: UsePositionsSubAccountsBlotterGroupingParams): UsePositionsSubAccountsBlotterGroupingOutput {
  const { groupings } = useRowGroupVariantSpecs();

  const { onStateUpdated: onGroupingStateUpdated, groupingToggleButtons } = useBlotterGroupingToggling({
    blotterName: 'sub account positions',
    setRowGroupColumns,
    groupings,
    allowNoGrouping: true,
    wrap,
    useMobileLabels,
  });

  return { onGroupingStateUpdated, groupingToggleButtons };
}
